import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Tabs, Tab, Breadcrumbs, Link, Alert, IconButton, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import "./ProviderAvailability.scss";
import NewRequest from "./NewRequest";
import SubmittedRequests from "./SubmittedRequests";
import { ApiCalls } from "../AllAppointmentsApi";
import { useSubmitApi } from "hooks/customHooks/authHook";
import { useMsal } from "@azure/msal-react";
import { BreadcrumbTexts, providerAvailabilitySuccessMessages, providerAvailbilityErrorMessages } from "constant/BiPortalConstants";
import { isTablet, isDesktop } from "react-device-detect";

export interface ProviderAvailabilityScheduleItem {
  isAdditionalShift: number;
  shiftId: number;
  shiftName: string;
  availableDays: number[];
  startTime: string;
  endTime: string;
  daysOfWeek: string;
}

interface TabConfig {
  label: string;
  component: React.ReactNode;
}

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`availability-tabpanel-${index}`}
      aria-labelledby={`availability-tab-${index}`}
      style={{
        display: value === index ? "block" : "none",
        minHeight: "300px",
      }}
      {...other}
    >
      <Box>{children}</Box>
    </div>
  );
}

const ProviderAvailability: React.FC = () => {
 
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
   
  const { getApiCall } = useSubmitApi();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState<number>(0);
  const [breadcrumbText, setBreadcrumbText] = useState<string>(BreadcrumbTexts.availability);
  const [resetNewRequest, setResetNewRequest] = useState<boolean>(false);
  const [providerCurrentShifts, setProviderCurrentShifts] = useState<ProviderAvailabilityScheduleItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [shiftDataError, setShiftDataError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submittedRequests, setSubmittedRequests] = useState<any[]>([]);
  const [isLoadingSubmittedRequests, setIsLoadingSubmittedRequests] = useState<boolean>(false);
  const hasFetchedRef = useRef<boolean>(false);
  const providerId = useRef<number | null>(null);
  const [providerInfo, setProviderInfo] = useState<{
    providerId: number;
    providerEmail: string;
    providerName: string;
    providerClinicName: string;
  } | null>(null);

  const handleShiftDataError = (errorMessage: string) => {
    setShiftDataError(errorMessage);
  };

  const fetchSubmittedRequests = useCallback(async () => {
    if (!providerId.current) return;
    
    setIsLoadingSubmittedRequests(true);
    try {
      const response = await getApiCall(ApiCalls.SubmittedRequestsCall, {
        providerId: providerId.current,
      });

      if (response?.data && Array.isArray(response.data)) {
        setSubmittedRequests(response.data);
      } else {
        setSubmittedRequests([]);
      }
    } catch (error) {
      console.error("Error fetching submitted requests:", error);
      setSubmittedRequests([]);
    } finally {
      setIsLoadingSubmittedRequests(false);
    }
  }, [getApiCall]);

  const handleRequestSuccess = useCallback(() => {
    setTabValue(1);
    setBreadcrumbText(BreadcrumbTexts.submittedRequests);
    setSuccessMessage(providerAvailabilitySuccessMessages);
    fetchSubmittedRequests();
  }, [fetchSubmittedRequests]);

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    if (successMessage) {
      const successTimer = setTimeout(() => {
        setSuccessMessage(null);
      }, 30000);
      timers.push(successTimer);
    }

    if (error) {
      const errorTimer = setTimeout(() => {
        setError(null);
      }, 30000);
      timers.push(errorTimer);
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        setSuccessMessage(null);
        setError(null);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      timers.forEach(timer => clearTimeout(timer));
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [successMessage, error]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    
    const fetchProviderCurrentShifts = async () => {
      try {
        const providerDetails = await getApiCall(ApiCalls.GetProviderDetailsCall, { email: activeAccount?.username });
        hasFetchedRef.current = true;
        providerId.current = providerDetails?.data?.id;
        
        if (providerDetails?.data) {
          setProviderInfo({
            providerId: providerDetails.data.id,
            providerEmail: providerDetails.data.email || activeAccount?.username || "",
            providerName: providerDetails.data.firstName + " " + providerDetails.data.lastName || "",
            providerClinicName: providerDetails.data.clinic.name || "",
          });
        }
        
        if (providerId.current) {
          const payload = {
            providerId: providerId.current,
          };
          setIsLoading(true);
          setError(null);
          const response = await getApiCall(ApiCalls.ProviderCurrentShiftsCall, payload);
          setProviderCurrentShifts(response.data);
        }
      } catch (error) {
        hasFetchedRef.current = true;
        setError(providerAvailbilityErrorMessages);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProviderCurrentShifts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBackToAvailability = () => {
    setTabValue(0);
    setBreadcrumbText(BreadcrumbTexts.availability);
    setResetNewRequest(true);
    setTimeout(() => setResetNewRequest(false), 0);
  };

  const tabs: TabConfig[] = useMemo(() => [
    {
      label: BreadcrumbTexts.newRequest,
      component: 
      <NewRequest 
        onBreadcrumbChange={setBreadcrumbText} 
        resetView={resetNewRequest} 
        providerCurrentShifts={providerCurrentShifts} 
        isLoading={isLoading} 
        providerId={providerId.current} 
        onShiftDataError={handleShiftDataError}
        providerInfo={providerInfo || undefined}
        onSuccess={handleRequestSuccess}
      />,
      },
      {
      label: BreadcrumbTexts.submittedRequests,
      component: <SubmittedRequests providerCurrentShifts={providerCurrentShifts} isLoading={isLoading} submittedRequestsData={submittedRequests} isLoadingRequests={isLoadingSubmittedRequests}/>,
    },
  ], [resetNewRequest, providerCurrentShifts, isLoading, providerInfo, submittedRequests, isLoadingSubmittedRequests, handleRequestSuccess]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0) {
      setBreadcrumbText(BreadcrumbTexts.availability);
      setResetNewRequest(true);
      setTimeout(() => setResetNewRequest(false), 0);
    } else if (newValue === 1) {
      setBreadcrumbText(BreadcrumbTexts.submittedRequests);
      fetchSubmittedRequests();
    }
    
    setTabValue(newValue);
  };

  return (
    <Box
      sx={{
        margin: { xs: "15px", sm: "20px", md: "25px" },
        maxWidth: "100%",
        overflowX: "hidden",
      }}
    >
      <Box className="breadcrumb" sx={{ mb: { xs: 2, md: 3 } }}>
        <Breadcrumbs
          separator="/"
          aria-label="breadcrumb"
          sx={{
            fontSize: { xs: "14px", sm: "16px" },
            "& .MuiBreadcrumbs-separator": {
            color: "#666",
            },
          }}
        >
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate("/providerappointments")}
            sx={{
              cursor: "pointer",
              color: "#666",
              fontSize: { xs: "14px", sm: "16px" },
              "&:hover": {
                color: "#10389A",
              },
            }}
          >
            Home
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => {
              handleBackToAvailability();
            }}
            sx={{
              cursor: "pointer",
              color: breadcrumbText !== BreadcrumbTexts.availability ? "#666" : "#000000DE",
              fontSize: { xs: "14px", sm: "16px" },
              "&:hover": {
                color: "#10389A",
              },
            }}
          >
            {BreadcrumbTexts.availability}
          </Link>
          {breadcrumbText !== BreadcrumbTexts.availability && (
            <Typography
              sx={{
                color: "#000000DE",
                fontSize: { xs: "14px", sm: "16px" },
              }}
            >
              {breadcrumbText}
            </Typography>
          )}
        </Breadcrumbs>
      </Box>

      {breadcrumbText === BreadcrumbTexts.clientWaitingForService && (
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Button
            variant="text"
            startIcon={<ArrowBackIosIcon />}
            onClick={handleBackToAvailability}
            sx={{
              color: "#12121299",
              textTransform: "none",
              fontSize: { xs: "14px", sm: "15px" },
              padding: { xs: "6px 12px", sm: "6px 12px" },
              minWidth: "auto",
              "&:hover": {
                backgroundColor: "rgba(16, 56, 154, 0.04)",
              },
            }}
          >
            Back
          </Button>
        </Box>
      )}

      <Box className= {!isTablet && !isDesktop ? "provider-availability-container-mobile" : "provider-availability-container"}>
        {successMessage && (
          <Alert
            severity="success"
            sx={{
              mb: 2,
              backgroundColor: "#D1F4E0",
              color: "#000000",
              border: "1px solid #A3D9B1",
              borderRadius: "8px",
              "& .MuiAlert-icon": {
                color: "#2E7D32",
              },
            }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setSuccessMessage(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: "#FDE8E8",
              color: "#5F2120",
              border: "1px solid #F5C2C7",
              borderRadius: "4px",
              "& .MuiAlert-icon": {
                color: "#D32F2F",
              },
            }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        )}
        {shiftDataError && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
              backgroundColor: "#FDE8E8",
              color: "#5F2120",
              border: "1px solid #F5C2C7",
              borderRadius: "4px",
              "& .MuiAlert-icon": {
                color: "#D32F2F",
              },
            }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setShiftDataError(null)}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
            }
          >
            {shiftDataError}
          </Alert>
        )}

        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                color: "#666666",
                fontSize: { xs: "14px", sm: "15px", md: "16px" },
                minWidth: { xs: "auto", sm: "120px" },
                padding: { xs: "12px 16px", sm: "12px 20px" },
                "&.Mui-selected": {
                  color: "#10389A",
                },
                "&:hover": {
                  color: "#10389A",
                },
                "&.Mui-disabled": {
                  color: "#9E9E9E",
                  opacity: 0.5,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: "#10389A",
              },
              "& .MuiTabs-scrollButtons": {
                "&.Mui-disabled": {
                  opacity: 0.3,
                },
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab 
                key={index} 
                label={tab.label} 
                disabled={index === 1 && isLoading}
              />
            ))}
          </Tabs>
        </Box>

        {tabs.map((tab, index) => (
          <TabPanel key={index} value={tabValue} index={index}>
            {tab.component}
        </TabPanel>
        ))}
      </Box>
    </Box>
  );
};

export default ProviderAvailability;
